/**
 * SearchesTable — the My Searches table.
 *
 * Columns: ID | Search | Status | Candidacies | Days open | Lead | Team |
 * People Lists | AgendaGPT. Deliberately NO totals row or aggregate strip: the
 * product guardrail is "focus on one search at a time", so the only derived
 * number on screen is each row's own `days_open`.
 *
 * CLIENT IS GONE (2026-08-27). It duplicated the Search column, whose name is
 * `<Client> - <Title>` for every search Thrive names properly — Jared: "I would
 * get rid of this column in here because it already says the Company right
 * here... it says it twice". The `client_company_name` field is still on the
 * wire and still feeds the `q` filter; only the column is dropped.
 *
 * TEAM answers "who else is on this" without costing a pixel of row height —
 * see `SearchTeamCell` for why that constraint produced an icon plus a count
 * rather than a face pile.
 *
 * AGENDAGPT sits to the RIGHT of People Lists (Jose's call, 2026-08-27). Both
 * are per-row actions, and the rightmost column is the one a reader scans to
 * last; the agenda is the newer, less-used of the two.
 *
 * The Thrive id is its own leftmost column rather than being folded into the
 * name (2026-08-07 feedback): a recruiter reading a search over the phone
 * needs it copy-pasteable, and it is the value they paste back into the search
 * box now that `q` matches it.
 *
 * The two links in a row point at DIFFERENT places, deliberately: the id is an
 * anchor out to Thrive, the name is a router `<Link>` in to our own Search
 * Detail (2026-08-13 — the name used to duplicate the id's Thrive link, which
 * left the detail page reachable only by clicking the row's empty space).
 * Keeping the name a real `<Link>` rather than a click handler is what makes
 * cmd+click and middle-click open it in a new tab.
 *
 * Both stop propagation on click and on Enter: the `<tr>` handles Enter/Space,
 * so an unguarded link fires both navigations at once (the trap epic sub-task
 * 03 documented). Only the Thrive anchor also stops Space — see each link's own
 * note. `preventDefault()` is NOT an alternative — it kills the link's own
 * navigation while every "does not navigate the row" assertion keeps passing
 * (mutation-verified, PR #640 finding C2).
 *
 * Own rows (`is_mine`) render the search name accent+bold, the same
 * attribution treatment the imports list uses for its own rows — it matters
 * under the Everyone filter, where the caller's searches are mixed in with
 * the firm's.
 *
 * Row click → Search Detail; the Imports CTA → the Imports list filtered by
 * this search. That CTA is a URL, deliberately: this feature knows the route
 * and the query param, and nothing about the imports feature's code.
 *
 * SORTING IS THE SERVER'S (Trello #813, 2026-09-28). This table renders the
 * rows in the order it is given and reports header clicks upward through
 * `onSort`; the page turns them into `sort` / `order` on the request, and the
 * API orders the whole result set before paging it. The table used to sort
 * the page it held under a "Sorted within this page only" caption — on prod
 * that was 139 Open searches as two pages that each ordered themselves, which
 * is what Jared's first piece of production feedback named. Caption, client
 * sort and `multiPage` are gone with it; the header buttons and their
 * `aria-sort` are unchanged.
 *
 * Design-system token utilities only.
 */
import type { SearchListItem, SearchesListSortKey } from "@nu-assist/contracts";
import { ArrowDown, ArrowUp, ArrowUpDown, List } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { SearchAgendaCell } from "../../../features/agenda/SearchAgendaCell";
import { SearchTeamCell } from "./SearchTeamCell";
import type { SearchesSort } from "./useSearchesList";

import { thriveSearchCandidatesUrl } from "../../../lib/thriveLinks";
import { searchDetailPath } from "../../../routing/paths";

export interface SearchesTableProps {
  items: readonly SearchListItem[];
  /** Rendered when the filters match nothing. */
  emptyMessage: string;
  /**
   * The caller's address, for the AgendaGPT column — the workflow mails the
   * agenda to it. Resolved by the page (`useMe`) rather than here so this
   * component stays presentational and rendering it needs no auth provider.
   * Empty (the default) disables the column's buttons rather than hiding them:
   * a missing control is unexplainable, a disabled one carries a `title` that
   * says to sign in again.
   */
  meEmail?: string;
  /** The sort the rows ARE in — the page owns it (URL-persisted). */
  sort: SearchesSort;
  /** A header click. The page toggles the direction and re-requests. */
  onSort: (key: SearchesListSortKey) => void;
}

type ColDef = { label: string; sortKey?: SearchesListSortKey };

const COLUMNS: ColDef[] = [
  { label: "ID" },
  { label: "Search", sortKey: "name" },
  { label: "Status", sortKey: "status" },
  { label: "Candidacies", sortKey: "candidacies_count" },
  { label: "Days open", sortKey: "days_open" },
  { label: "Lead", sortKey: "lead_name" },
  { label: "Team" },
  { label: "People Lists" },
  { label: "AgendaGPT" },
];

/** The sortable columns, derived from `COLUMNS` so a column cannot gain a
 *  header button while staying unaddressable from `?sort=` (or vice versa) —
 *  the same discipline `STATUS_FILTERS` applies to the pills. */
export const SORT_KEYS: readonly SearchesListSortKey[] = COLUMNS.flatMap((c) =>
  c.sortKey ? [c.sortKey] : []
);

/** Whether a URL value names a sortable column; anything else is "no sort". */
export function isSortKey(value: string | null): value is SearchesListSortKey {
  return value !== null && (SORT_KEYS as readonly string[]).includes(value);
}

/** `/research/imports` filtered by one search (the name is display-only). */
function importsHref(row: SearchListItem): string {
  const params = new URLSearchParams({ search_id_thrive: row.id_thrive });
  if (row.name) params.set("search_name", row.name);
  return `/research/imports?${params.toString()}`;
}

/** Status pill tone. Open is the live-work state; BD is a prospect; anything
 *  else (Closed, Lost) is history and reads muted.
 *
 *  Accepts `undefined` as well as `null`: `openapi-typescript` emits nullable
 *  Pydantic fields as OPTIONAL properties, so every nullable column arrives as
 *  `T | null | undefined` on the generated type. */
function statusClasses(status: string | null | undefined): string {
  const base = "inline-block rounded-full px-2 py-0.5 text-label";
  if (status === "Open") return `${base} bg-success-muted text-success`;
  if (status === "Business Development") return `${base} bg-accent-muted text-accent`;
  return `${base} bg-surface-raised text-text-muted`;
}

/**
 * An anchor to the search on Thrive that lives inside a clickable row.
 *
 * Both handlers are needed. `onClick` covers the pointer; `onKeyDown` covers
 * the keyboard, because the `<tr>` treats Enter/Space as "open the detail" and
 * the event bubbles — so without it one Enter on a focused link opens Thrive in
 * a new tab AND navigates the row underneath it.
 */
function ThriveSearchLink({
  searchId,
  label,
  testId,
  className,
}: {
  searchId: string;
  label: string;
  testId: string;
  className: string;
}) {
  return (
    <a
      href={thriveSearchCandidatesUrl(searchId)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      title={`Open search ${searchId} in Thrive`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
      className={className}
    >
      {label}
    </a>
  );
}

/**
 * A router link to our own Search Detail that lives inside a clickable row.
 *
 * Guards the click, and guards ENTER only — deliberately narrower than
 * `ThriveSearchLink`'s. The `<tr>` navigates on both Enter and Space, and an
 * anchor activates on Enter alone, so:
 *
 * - Enter would fire this link's navigation AND the row's, and the second wins
 *   (the trap epic sub-task 03 documented) — hence the guard;
 * - Space activates no anchor at all, so stopping it only made the key dead
 *   while the name cell had focus. Letting it reach the row is what gives it a
 *   behaviour, and the row's destination is THIS link's destination, so the
 *   keyboard user lands where the visible affordance promised.
 *
 * `ThriveSearchLink` keeps both guards because its destination is different:
 * Space falling through there would leave for our detail page from a control
 * labelled "open this in Thrive".
 *
 * Note what neither may do: `preventDefault()` leaves the row alone by killing
 * the link itself, and every "does not navigate the row" assertion keeps
 * passing (mutation-verified, PR #640 finding C2).
 */
function SearchDetailLink({
  searchId,
  label,
  className,
}: {
  searchId: string;
  label: string;
  className: string;
}) {
  return (
    <Link
      to={searchDetailPath(searchId)}
      data-testid="searches-row-name"
      title={`Open the nu-assist detail for ${label}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.stopPropagation();
      }}
      className={className}
    >
      {label}
    </Link>
  );
}

export function SearchesTable({
  items,
  emptyMessage,
  meEmail = "",
  sort,
  onSort,
}: SearchesTableProps) {
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div
        data-testid="searches-empty"
        className="rounded-md border border-border bg-surface px-4 py-12 text-center text-body text-text-secondary"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
      <table className="w-full border-collapse" data-testid="searches-table">
        <thead>
          <tr className="border-b border-border text-left">
            {COLUMNS.map((col) => {
              const active = col.sortKey ? sort?.key === col.sortKey : false;
              const sortable = !!col.sortKey;
              const dir = sort?.dir ?? "asc";
              return (
                <th
                  key={col.label}
                  scope="col"
                  className="text-label font-semibold text-text-secondary"
                  aria-sort={
                    active
                      ? dir === "asc"
                        ? "ascending"
                        : "descending"
                      : sortable
                        ? "none"
                        : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={`inline-flex w-full items-center gap-1 px-3 py-2 text-left cursor-pointer select-none hover:text-text${active ? " text-accent" : ""}`}
                      onClick={() => onSort(col.sortKey as SearchesListSortKey)}
                    >
                      {col.label}
                      {active ? (
                        dir === "asc" ? (
                          <ArrowUp aria-hidden="true" className="h-3 w-3 text-accent" />
                        ) : (
                          <ArrowDown aria-hidden="true" className="h-3 w-3 text-accent" />
                        )
                      ) : (
                        <ArrowUpDown aria-hidden="true" className="h-3 w-3 text-text-muted" />
                      )}
                    </button>
                  ) : (
                    <span className="block px-3 py-2">{col.label}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const open = () => navigate(searchDetailPath(row.id_thrive));
            return (
              <tr
                key={row.id_thrive}
                data-testid="searches-row"
                data-own={row.is_mine ? "true" : "false"}
                data-search-id={row.id_thrive}
                tabIndex={0}
                // Names the ROW's action. It now coincides with the name cell's
                // — both open the Search Detail — but the id anchor inside
                // still leaves for Thrive, so the label stays specific about
                // which of the two destinations activating the row reaches.
                aria-label={`Open search detail for ${row.name}`}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                  }
                }}
                className="cursor-pointer border-b border-border last:border-b-0 hover:bg-row-hover focus:outline-none focus:bg-row-hover"
              >
                <td className="whitespace-nowrap px-3 py-2 text-body tabular-nums">
                  <ThriveSearchLink
                    searchId={row.id_thrive}
                    label={row.id_thrive}
                    testId="searches-row-thrive-id"
                    className="text-text-secondary underline decoration-dotted underline-offset-2 hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </td>
                <td className="px-3 py-2 text-body text-text">
                  <SearchDetailLink
                    searchId={row.id_thrive}
                    label={row.name}
                    className={`hover:underline focus:outline-none focus:ring-1 focus:ring-accent ${
                      row.is_mine ? "text-accent font-semibold" : "text-text"
                    }`}
                  />
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <span className={statusClasses(row.status)}>{row.status ?? "Unknown"}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-body text-text-secondary">
                  {/* Null means Thrive's counter was unusable (it is
                      denormalized and can go negative). The em dash is this
                      table's existing "we do not know" — same as `days_open`
                      and the toolbar count. Never render 0 here: it reads as a
                      fact, and the searches that hit this have had dozens of
                      candidates. */}
                  {row.candidacies_count === null || row.candidacies_count === undefined
                    ? "—"
                    : row.candidacies_count.toLocaleString()}
                </td>
                <td
                  className="whitespace-nowrap px-3 py-2 text-body text-text-secondary"
                  data-testid="searches-row-days-open"
                >
                  {row.days_open === null || row.days_open === undefined
                    ? "—"
                    : row.days_open.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-body text-text-secondary">
                  {row.lead_name ?? row.lead_email ?? "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-body">
                  {/* The roster ships WITH the list (`team_members`), so this
                      cell costs no request of its own — 25 of them on a page
                      is still the one list call. */}
                  <SearchTeamCell members={row.team_members ?? []} searchName={row.name} />
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {/* Both the click AND the key event are stopped: the row
                      opens the detail on Enter/Space, so without the keydown
                      guard one Enter on this button would fire both
                      navigations. */}
                  <button
                    type="button"
                    aria-label={`View people lists for ${row.name}`}
                    title={`View people lists for ${row.name}`}
                    data-testid="searches-row-imports"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(importsHref(row));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                    }}
                    className="inline-flex items-center justify-center rounded-md p-1.5 text-text-secondary hover:bg-accent-muted hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    {/* `List`, not `Inbox` (Lauren via Jared, 2026-10-07). */}
                    <List className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <SearchAgendaCell
                    searchId={row.id_thrive}
                    searchName={row.name}
                    meEmail={meEmail}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
